class AdvancedSlider {
  constructor(slider) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    this.slides = Array.from(this.track.children);
    this.dotsContainer = null;
    this.touch = { startX: 0, active: false };
    this.autoplayTimer = null;
    this.currentIndex = 0;
    this.enabled = true; // active at current breakpoint?

    this.settings = {
      arrows: slider.dataset.arrows === 'true',
      dots: slider.dataset.dots === 'true',
      draggable: slider.dataset.draggable === 'true',
      loop: slider.dataset.loop === 'true',
      autoplay: slider.dataset.autoplay === 'true',
      autoplayDelay: parseInt(slider.dataset.autoplaydelay || '4000'),
      gap: parseInt(slider.dataset.gap || '15'),
      slidesDesktop: parseFloat(slider.dataset.slidesDesktop || '3'),
      slidesTablet: parseFloat(slider.dataset.slidesTablet || '2'),
      slidesMobile: parseFloat(slider.dataset.slidesMobile || '1'),
      scrollDesktop: parseFloat(slider.dataset.scrollDesktop || '1'),
      scrollTablet: parseFloat(slider.dataset.scrollTablet || '1'),
      scrollMobile: parseFloat(slider.dataset.scrollMobile || '1'),
      // NEW:
      disableDesktop: slider.dataset.disableDesktop === 'true',
      disableTablet: slider.dataset.disableTablet === 'true',
      disableMobile: slider.dataset.disableMobile === 'true'
    };

    this.setResponsive();
    this.setup();
  }

  setup() {
    if (this.slides.length <= 1) { this.teardown(); return; }
    this.createControls();
    this.attachEvents();
    this.update(true);
    if (this.settings.autoplay && this.enabled) this.startAutoplay();
  }

  setResponsive() {
    const width = window.innerWidth;
    if (width >= 1024) {
      this.enabled = !this.settings.disableDesktop;
      this.slidesToShow = this.settings.slidesDesktop;
      this.slidesToScroll = this.settings.scrollDesktop;
    } else if (width >= 768) {
      this.enabled = !this.settings.disableTablet;
      this.slidesToShow = this.settings.slidesTablet;
      this.slidesToScroll = this.settings.scrollTablet;
    } else {
      this.enabled = !this.settings.disableMobile;
      this.slidesToShow = this.settings.slidesMobile;
      this.slidesToScroll = this.settings.scrollMobile;
    }
  }

  createControls() {
    if (this.settings.arrows) {
      if (!this.slider.querySelector('.slider-arrow.prev')) this.createArrow('prev');
      if (!this.slider.querySelector('.slider-arrow.next')) this.createArrow('next');
    }

    if (this.settings.dots && this.slides.length > this.slidesToShow) {
      if (!this.dotsContainer) {
        this.dotsContainer = document.createElement('div');
        this.dotsContainer.className = 'slider-dots';
        this.slider.appendChild(this.dotsContainer);
      }
      this.rebuildDots();
    } else if (this.dotsContainer) {
      this.dotsContainer.style.display = 'none';
    }

    this.ensureControlsVisibility();
  }

  ensureControlsVisibility() {
    const arrows = this.slider.querySelectorAll('.slider-arrow');
    arrows.forEach(btn => { btn.style.display = this.enabled && this.settings.arrows ? '' : 'none'; });
    if (this.dotsContainer) {
      const showDots = this.enabled && this.settings.dots && (this.slides.length > this.slidesToShow);
      this.dotsContainer.style.display = showDots ? 'flex' : 'none';
    }
  }

  createArrow(direction) {
    const button = document.createElement('button');
    button.className = `slider-arrow ${direction}`;
    button.innerHTML = direction === 'prev'
      ? `<svg viewBox="0 0 40 40"><path d="M22.5 12.5 15 20l7.5 7.5" stroke="white" stroke-width="2"/></svg>`
      : `<svg viewBox="0 0 40 40"><path d="M17.5 12.5 25 20l-7.5 7.5" stroke="white" stroke-width="2"/></svg>`;
    button.addEventListener('click', () => this.move(direction === 'prev' ? -1 : 1));
    this.slider.appendChild(button);
  }

  rebuildDots() {
    if (!this.dotsContainer) return;
    const totalDots = Math.ceil((this.slides.length - this.slidesToShow) / this.slidesToScroll) + 1;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'dot';
      dot.addEventListener('click', () => {
        this.currentIndex = i * this.slidesToScroll;
        this.update();
        this.restartAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.updateDots();
  }

  attachEvents() {
    window.addEventListener('resize', () => {
      const prevEnabled = this.enabled;
      this.setResponsive();
      if (this.enabled !== prevEnabled) {
        if (this.enabled) {
          this.createControls();
          this.update(true);
          this.restartAutoplay();
        } else {
          this.teardown();
        }
      } else {
        this.update(true);
        this.ensureControlsVisibility();
      }
    });

    if (this.settings.draggable) {
      this.track.addEventListener('mousedown', (e) => this.dragStart(e));
      this.track.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
      this.track.addEventListener('mousemove', (e) => this.dragMove(e));
      this.track.addEventListener('touchmove', (e) => this.dragMove(e), { passive: true });
      this.track.addEventListener('mouseup', () => this.dragEnd());
      this.track.addEventListener('touchend', () => this.dragEnd());
      this.track.addEventListener('mouseleave', () => this.dragEnd());
    }
  }

  teardown() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;

    this.track.style.transition = 'none';
    this.track.style.transform = 'none';
    this.track.style.gap = '';

    this.slides.forEach(slide => {
      slide.style.flex = '';
      slide.style.width = '';
    });

    const arrows = this.slider.querySelectorAll('.slider-arrow');
    arrows.forEach(btn => { btn.style.display = 'none'; });
    if (this.dotsContainer) this.dotsContainer.style.display = 'none';
  }

  update(skipTransition = false) {
    if (!this.enabled) { this.teardown(); return; }

    const visibleSlides = this.slidesToShow;
    const totalSlides = this.slides.length;
    const containerWidth = this.slider.clientWidth || this.track.clientWidth;
    const slideWidthPx = (containerWidth - (this.settings.gap * (visibleSlides - 1))) / visibleSlides;

    this.slides.forEach(slide => {
      slide.style.flex = `0 0 ${slideWidthPx}px`;
      slide.style.width = `${slideWidthPx}px`;
    });

    const maxIndex = Math.max(0, Math.ceil((totalSlides - visibleSlides) / this.slidesToScroll));
    if (!this.settings.loop) {
      this.currentIndex = Math.max(0, Math.min(this.currentIndex, maxIndex));
    } else {
      if (this.currentIndex > maxIndex) this.currentIndex = 0;
      if (this.currentIndex < 0) this.currentIndex = maxIndex;
    }

    const translateX = (slideWidthPx + this.settings.gap) * this.currentIndex * -1;
    this.track.style.transition = skipTransition ? 'none' : 'transform 0.5s ease';
    this.track.style.transform = `translateX(${translateX}px)`;
    this.track.style.gap = `${this.settings.gap}px`;

    this.updateDots();
    this.ensureControlsVisibility();
  }

  updateDots() {
    if (!this.dotsContainer || !this.enabled) return;
    const dots = Array.from(this.dotsContainer.children);
    dots.forEach(dot => dot.classList.remove('active'));
    const activeDot = Math.round(this.currentIndex / this.slidesToScroll);
    if (dots[activeDot]) dots[activeDot].classList.add('active');
  }

  move(direction) {
    if (!this.enabled) return;
    this.currentIndex += direction * this.slidesToScroll;
    this.update();
    this.restartAutoplay();
  }

  dragStart(e) {
    if (!this.enabled) return;
    this.touch.active = true;
    this.touch.startX = e.touches ? e.touches[0].pageX : e.pageX;
  }

  dragMove(e) {
    if (!this.enabled || !this.touch.active) return;
    const currentX = e.touches ? e.touches[0].pageX : e.pageX;
    const dist = this.touch.startX - currentX;
    if (Math.abs(dist) > 50) {
      this.move(dist > 0 ? 1 : -1);
      this.touch.active = false;
    }
  }

  dragEnd() {
    this.touch.active = false;
  }

  startAutoplay() {
    if (!this.enabled) return;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = setInterval(() => this.move(1), this.settings.autoplayDelay);
  }

  restartAutoplay() {
    if (!this.settings.autoplay) return;
    clearInterval(this.autoplayTimer);
    if (this.enabled) this.startAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.new-custom-slider').forEach(slider => new AdvancedSlider(slider));
});
